<template>
  <div>
    <div class="express-checkout-container" v-if="status !== 'success'">
      <ExpressCheckoutElement
        v-if="stripe && elements && !initializationFailed"
        :stripe="stripe"
        :elements="elements"
        :is-loading="isLoading"
        :currency="currency"
        @click="handleExpressCheckoutClick"
        @confirm="handleExpressCheckoutConfirm"
        @cancel="handleExpressCheckoutCancel"
      />
      <div v-else-if="!initializationFailed" class="stripe-loading">
        <div class="loading-placeholder">
          {{ $t('exit_popup.loading_button') }}
        </div>
      </div>
      <div v-else-if="false" class="stripe-error">
        <div class="error-placeholder">
          {{ $t('INVALID STRIPE INIT') }}
        </div>
      </div>
    </div>
    <PaymentConfirmation
      :status="status"
      :error-message="errorMessage"
    />
  </div>
</template>

<script>
import ExpressCheckoutElement from "./stripe/ExpressCheckoutElement";
import PaymentConfirmation from "./stripe/PaymentConfirmation";
import StripeServiceMixin from "./stripe/StripeServiceMixin";
import PaymentValidationMixin from "./stripe/PaymentValidationMixin";

export default {
  name: 'StripePayment',
  mixins: [StripeServiceMixin, PaymentValidationMixin],
  props: ["quantity", "sku", "amount", "upsells", "warranty", "onSuccess", "paymentFormData", "$v"],
  components: { 
    ExpressCheckoutElement,
    PaymentConfirmation
  },
  data() {
    return {
      isLoading: false,
      paymentIntent: null,
      stripe: null,
      elements: null,
      method: "google_pay",
      status: null, // defines which message display in template
      errorMessage: "",
      order_id: null,
      order_currency: null,
      order_number: null,
      odin_order_id: null,
      initializationFailed: false
    };
  },

  computed: {
    currency() {
      return !js_query_params.cur || js_query_params.cur === "{aff_currency}" ? js_data.product.prices.currency.toLowerCase() : js_query_params.cur.toLowerCase();
    },

    // we need to provide amount as integer, so first we need to multiply currency by 100, and leave some currencies without multiplier
    currencyMultiplier() {
      // TODO: use currencies list from StripeAmountMapper
      const noMult = ["BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF"];
      return noMult.indexOf(this.currency.toUpperCase()) === -1 ? 100 : 1;
    },

    intentRequestUri() {
      if (this.upsells) {
        return "/pay-by-card-upsells";
      } else {
        return "/pay-by-wallet";
      }
    }
  },

  watch: {
    // watch for amount changes (radio buttons, warranty checkbox, etc.)
    amount: {
      handler(newAmount, oldAmount) {
        if (newAmount !== oldAmount && this.elements) {
          this.updatePaymentAmount(newAmount);
        }
      },
      immediate: false
    }
  },

  async mounted() {
    try {
      await this.initStripe();
    } catch (error) {
      console.warn('Stripe initialization failed:', error);
      this.initializationFailed = true;
    }
  },

  methods: {
    handleExpressCheckoutClick(event) {
      try {
        this.$emit('click');
        
        // validate deal selection (quantity/article) first
        if (!this.validateDealSelection()) {
          console.log('Deal selection validation failed, preventing wallet popup');
          // don't call resolve at all - let it timeout to prevent popup
          return;
        }
        
        // validate variant selection if required
        if (!this.validateVariantSelection()) {
          console.log('Variant selection validation failed, preventing wallet popup');
          // don't call resolve at all - let it timeout to prevent popup
          return;
        }
        
        // for Google Pay, skip personal data validation since Google Pay will collect this info
        // for other wallet methods, validate required personal data fields before proceeding
        if (this.method !== 'google_pay' && !this.validatePersonalData()) {
          console.log('Personal data validation failed, preventing wallet popup');
          // don't call resolve at all - let it timeout to prevent popup
          return;
        }
        
        this.isLoading = true;
        
        // only resolve if validation passes
        event.resolve();
      } catch (error) {
        console.error('Error during click validation:', error);
        this.isLoading = false;
        // don't resolve on error - let it timeout to prevent popup
      }
    },

    async handleExpressCheckoutConfirm(event) {
      try {
        const { error: submitError } = await this.elements.submit();
        if (submitError) {
          this.handlePayment({ error: submitError });
          this.isLoading = false;
          return;
        }

        // create payment intent when confirming payment
        if (!this.paymentIntent || !this.paymentIntent.client_secret) {
          const response = await this.createPaymentIntent();
          if (!response || response.error) {
            this.handlePayment({ error: { message: 'Failed to create payment intent: ' + (response?.error || 'Unknown error') } });
            this.isLoading = false;
            return;
          }
          // payment intent is set in createPaymentIntent method
        }

        // check if payment intent has valid client_secret
        if (!this.paymentIntent.client_secret) {
          console.error('Invalid payment intent:', this.paymentIntent);
          this.handlePayment({ error: { message: 'Invalid payment intent - no client secret' } });
          this.isLoading = false;
          return;
        }

        // check for payment intent errors (status should be requires_payment_method for new intents)
        if (this.paymentIntent.id && this.paymentIntent.id.startsWith('fail_')) {
          console.error('Failed payment intent:', this.paymentIntent);
          this.handlePayment({ error: { message: 'Payment intent creation failed' } });
          this.isLoading = false;
          return;
        }

        // for Express Checkout Elements with automatic_payment_methods,
        // we need to use the newer confirmPayment method
        const confirmOptions = {
          elements: this.elements,
          clientSecret: this.paymentIntent.client_secret,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required'
        };

        // add shipping information from Google Pay if available
        if (event.shippingAddress) {
          confirmOptions.confirmParams.shipping = {
            name: event.shippingAddress.name,
            phone: event.shippingAddress.phone,
            address: {
              line1: event.shippingAddress.address.line1,
              city: event.shippingAddress.address.city,
              postal_code: event.shippingAddress.address.postal_code,
              state: event.shippingAddress.address.state,
              country: event.shippingAddress.address.country
            }
          };

          // save data from Google Pay response including address
          this.saveUserDataToStorage(
            event.shippingAddress.name,
            null, // email not available in shipping address
            event.shippingAddress.phone
          );
          
          // save shipping address data for customer creation
          this.saveShippingAddressToStorage(event.shippingAddress.address);
        }

        // extract billing details from Google Pay
        if (event.billingDetails) {
          
          // save billing details from Google Pay to localStorage
          this.saveUserDataToStorage(
            event.billingDetails.name,
            event.billingDetails.email,
            event.billingDetails.phone
          );

          // save billing address if shipping address is not available
          if (!event.shippingAddress && event.billingDetails.address) {
            this.saveShippingAddressToStorage(event.billingDetails.address);
          }

          // add billing details to payment confirmation
          const billingData = {};
          if (event.billingDetails.name) billingData.name = event.billingDetails.name;
          if (event.billingDetails.email) billingData.email = event.billingDetails.email;
          if (event.billingDetails.phone) billingData.phone = event.billingDetails.phone;
          
          if (event.billingDetails.address) {
            billingData.address = event.billingDetails.address;
          }
          
          if (Object.keys(billingData).length > 0) {
            confirmOptions.confirmParams.payment_method_data = {
              billing_details: billingData
            };
          }
        }
        const { error, paymentIntent } = await this.stripe.confirmPayment(confirmOptions);
        
        this.handlePayment({ error, paymentIntent });
        this.isLoading = false;
      } catch (error) {
        console.error('Error during payment confirmation:', error);
        this.handlePayment({
          error: {
            message: error.message || 'Payment confirmation failed'
          }
        });
        this.isLoading = false;
      }
    },

    handleExpressCheckoutCancel() {
      this.isLoading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.express-checkout-container {
  margin-bottom: 20px;

  .stripe-loading {
    .loading-placeholder {
      min-height: 55px;
      background: #f8f9fa;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #6c757d;
      font-size: 14px;
    }
  }

  .stripe-error {
    .error-placeholder {
      min-height: 55px;
      background: #f8d7da;
      border: 1px solid #f5c6cb;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #721c24;
      font-size: 14px;
    }
  }
}
</style>